import {
  dataLabelsCompact,
  dataLabelsDecimals,
  dataLabelsFlipHorizontally,
  dataLabelsHideThreshold,
  dataLabelsSizes,
  legendPosition,
  palette,
  showDataLabels,
  showItems,
} from "@/settings/charts/settingsDictionary";
import { FAMILY } from "@/settings/widgetFamilies";
import { Config } from "@/types/charts";

const stackedColumnLineSettings = {
  palette,
  xAxis: {
    default: "channel",
    type: "select",
    label: "Horizontal Axis (Decomposition)",
    dictionary: "dimensionMetrics",
    apiParameter: "dimension",
    gridPosition: [0, 0],
    tooltipOrder: 0,
  },
  displayMetrics: {
    default: ["leads", "acquisitions"],
    type: "select",
    label: "Display Metrics (Stacked Bars)",
    dictionary: "displayMetrics",
    multiple: true,
    searchable: true,
    gridPosition: [1, 0],
    tooltipOrder: 1,
  },
  lineMetric: {
    default: "spend",
    type: "select",
    label: "Display Metric (Line)",
    dictionary: "displayMetrics",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  legendPosition: { ...legendPosition, gridPosition: [3, 0] },
  xAxisName: {
    default: "",
    type: "text",
    label: "Horizontal Axis Title",
    gridPosition: [0, 1],
    forAxis: "xAxis",
  },
  yAxisName: {
    default: "",
    type: "text",
    label: "Vertical Axis Title",
    gridPosition: [1, 1],
  },
};

const stackedColumnLineExploreSettings = {
  ...stackedColumnLineSettings,
  showItems: { ...showItems, gridPosition: [3, 0] },
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
};

const stackedColumnLinePlanExploreSettings = {
  ...stackedColumnLineExploreSettings,
  showDataLabels: { ...showDataLabels, gridPosition: [5, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [5, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [6, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [6, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [7, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [7, 0] },
};

const stackedColumnLinePlanSettings = {
  ...stackedColumnLineSettings,
  displayMetrics: { ...stackedColumnLineSettings.displayMetrics, default: ["impressions", "clicks"] },
  lineMetric: { ...stackedColumnLineSettings.lineMetric, default: "channel_budget" },
  showItems: { ...showItems, gridPosition: [3, 0] },
  legendPosition: { ...legendPosition, gridPosition: [4, 0] },
  showDataLabels: { ...showDataLabels, gridPosition: [5, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [5, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [6, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [6, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [7, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [7, 0] },
};

const stackedColumnLineDualSettings = {
  palette,
  displayMetricsSource: { default: "explore", dictionary: "metricSources" },
  lineMetricSource: { default: "plan", dictionary: "metricSources" },
  xAxis: {
    default: "channel",
    type: "select",
    label: "Grouping Dimension (Project + Explore)",
    dictionary: "dualDimensionMetrics",
    apiParameter: "dimension",
    gridPosition: [0, 0],
    tooltipOrder: 0,
  },
  displayMetrics: {
    default: ["impressions"],
    type: "select",
    label: "Display Metrics (Stacked Bars)",
    dictionary: "displayMetrics",
    multiple: true,
    searchable: true,
    sourceFrom: "displayMetricsSource",
    gridPosition: [1, 0],
    tooltipOrder: 1,
  },
  lineMetric: {
    default: "channel_budget",
    defaultBySource: { plan: "channel_budget", explore: "spend" },
    type: "select",
    label: "Display Metric (Line)",
    dictionary: "displayMetrics",
    sourceFrom: "lineMetricSource",
    gridPosition: [2, 0],
    tooltipOrder: 2,
  },
  legendPosition: { ...legendPosition, gridPosition: [3, 0] },
  xAxisName: {
    default: "",
    type: "text",
    label: "Horizontal Axis Title",
    gridPosition: [0, 1],
    forAxis: "xAxis",
  },
  yAxisName: {
    default: "",
    type: "text",
    label: "Vertical Axis Title",
    gridPosition: [1, 1],
  },
  showDataLabels: { ...showDataLabels, gridPosition: [4, 0] },
  dataLabelsSizes: { ...dataLabelsSizes, gridPosition: [4, 1] },
  dataLabelsCompact: { ...dataLabelsCompact, gridPosition: [5, 0] },
  dataLabelsDecimals: { ...dataLabelsDecimals, gridPosition: [5, 1] },
  dataLabelsHideThreshold: { ...dataLabelsHideThreshold, gridPosition: [6, 1] },
  dataLabelsFlipHorizontally: { ...dataLabelsFlipHorizontally, gridPosition: [6, 0] },
};

export default <Config>{
  family: [FAMILY.COMPARISON],
  name: "stackedColumnLineChart",
  title: "Stacked Column & Line Type 2",
  defaultHeight: 6,
  dataModes: ["project", "explore", "dual"],
  settings: stackedColumnLineExploreSettings,
  planSettings: stackedColumnLinePlanSettings,
  planExploreSettings: stackedColumnLinePlanExploreSettings,
  planDualSettings: stackedColumnLineDualSettings,
};
